import { Link } from "@tanstack/react-router";
import { Plus, Check } from "lucide-react";
import { useState } from "react";
import { useInquiry } from "@/lib/inquiry-store";
import { fallbackImage, type Product } from "@/lib/catalog";

export function ProductCard({
  product,
  categoryHandle,
  categoryName,
}: {
  product: Product;
  categoryHandle?: string;
  categoryName?: string;
}) {
  const { add } = useInquiry();
  const [added, setAdded] = useState(false);
  const img = product.image_url || fallbackImage(categoryHandle);

  const onAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    add({
      productHandle: product.handle,
      productName: product.name,
      categoryName,
      imageUrl: img,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div className="group flex flex-col bg-[#FAF9F6] p-3 rounded-[24px] transition-shadow hover:shadow-sm border border-border/50">
      <Link
        to="/products/$handle"
        params={{ handle: product.handle }}
        className="block overflow-hidden bg-white rounded-[16px]"
      >
        <img
          src={img}
          alt={product.name}
          title={product.name}
          width={400}
          height={400}
          loading="lazy"
          className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="mt-4 flex flex-1 flex-col">
        {categoryName && (
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            {categoryName}
          </p>
        )}
        <Link
          to="/products/$handle"
          params={{ handle: product.handle }}
          className="mt-1 font-serif text-lg leading-snug hover:text-primary"
        >
          {product.name}
        </Link>
        {product.short_description && (
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
            {product.short_description}
          </p>
        )}
        {(product.specs?.["2025 Stock Price"] || product.specs?.["2026 Stock Price"]) && (
          <div className="mt-2 flex flex-col gap-0.5 text-sm">
            {product.specs?.["2025 Stock Price"] && (
              <span className="font-medium text-foreground">
                {product.specs["2025 Stock Price"]}
              </span>
            )}
            {product.specs?.["2026 Stock Price"] && (
              <span className="font-medium text-foreground">
                {product.specs["2026 Stock Price"]}
              </span>
            )}
          </div>
        )}
        <button
          onClick={onAdd}
          className="mt-4 inline-flex items-center justify-center gap-2 self-start rounded-sm border border-border px-4 py-2 text-xs font-medium uppercase tracking-[0.15em] transition-colors hover:border-primary hover:text-primary"
        >
          {added ? (
            <>
              <Check className="h-3.5 w-3.5" /> Added
            </>
          ) : (
            <>
              <Plus className="h-3.5 w-3.5" /> Add to Inquiry
            </>
          )}
        </button>
      </div>
    </div>
  );
}
